/**
 * Location data for onboarding and leaderboard scopes.
 *
 * Storage convention (shared with the server — see backend/api-types.ts):
 * - `country` is an ISO 3166-1 alpha-2 code ("ZA").
 * - `province` is the ISO 3166-2 subdivision code without the country prefix
 *   for South Africa ("GP", "WC", …), so English and Afrikaans players share one
 *   province board. For other countries it is free text (may be empty).
 * - `city` is free text; for South Africa we suggest real towns (current
 *   official names) so most players pick the same spelling.
 */
import type { Locale } from '@dropstack/core';
import { foldText, rankMatches } from './text.ts';

export type LocalizedName = Record<Locale, string>;

export interface Country {
  code: string;
  name: LocalizedName;
}

export interface Province {
  code: SaProvinceCode;
  name: LocalizedName;
}

export const HOME_COUNTRY = 'ZA';

/** South Africa first, then common countries alphabetically by English name. */
export const COUNTRIES: readonly Country[] = [
  { code: 'ZA', name: { en: 'South Africa', af: 'Suid-Afrika' } },
  { code: 'AO', name: { en: 'Angola', af: 'Angola' } },
  { code: 'AR', name: { en: 'Argentina', af: 'Argentinië' } },
  { code: 'AU', name: { en: 'Australia', af: 'Australië' } },
  { code: 'BE', name: { en: 'Belgium', af: 'België' } },
  { code: 'BW', name: { en: 'Botswana', af: 'Botswana' } },
  { code: 'BR', name: { en: 'Brazil', af: 'Brasilië' } },
  { code: 'CA', name: { en: 'Canada', af: 'Kanada' } },
  { code: 'CN', name: { en: 'China', af: 'China' } },
  { code: 'DK', name: { en: 'Denmark', af: 'Denemarke' } },
  { code: 'EG', name: { en: 'Egypt', af: 'Egipte' } },
  { code: 'SZ', name: { en: 'Eswatini', af: 'Eswatini' } },
  { code: 'FR', name: { en: 'France', af: 'Frankryk' } },
  { code: 'DE', name: { en: 'Germany', af: 'Duitsland' } },
  { code: 'GH', name: { en: 'Ghana', af: 'Ghana' } },
  { code: 'IN', name: { en: 'India', af: 'Indië' } },
  { code: 'IE', name: { en: 'Ireland', af: 'Ierland' } },
  { code: 'IT', name: { en: 'Italy', af: 'Italië' } },
  { code: 'JP', name: { en: 'Japan', af: 'Japan' } },
  { code: 'KE', name: { en: 'Kenya', af: 'Kenia' } },
  { code: 'LS', name: { en: 'Lesotho', af: 'Lesotho' } },
  { code: 'MW', name: { en: 'Malawi', af: 'Malawi' } },
  { code: 'MU', name: { en: 'Mauritius', af: 'Mauritius' } },
  { code: 'MZ', name: { en: 'Mozambique', af: 'Mosambiek' } },
  { code: 'NA', name: { en: 'Namibia', af: 'Namibië' } },
  { code: 'NL', name: { en: 'Netherlands', af: 'Nederland' } },
  { code: 'NZ', name: { en: 'New Zealand', af: 'Nieu-Seeland' } },
  { code: 'NG', name: { en: 'Nigeria', af: 'Nigerië' } },
  { code: 'NO', name: { en: 'Norway', af: 'Noorweë' } },
  { code: 'PT', name: { en: 'Portugal', af: 'Portugal' } },
  { code: 'QA', name: { en: 'Qatar', af: 'Katar' } },
  { code: 'SA', name: { en: 'Saudi Arabia', af: 'Saoedi-Arabië' } },
  { code: 'SG', name: { en: 'Singapore', af: 'Singapoer' } },
  { code: 'ES', name: { en: 'Spain', af: 'Spanje' } },
  { code: 'SE', name: { en: 'Sweden', af: 'Swede' } },
  { code: 'CH', name: { en: 'Switzerland', af: 'Switserland' } },
  { code: 'TZ', name: { en: 'Tanzania', af: 'Tanzanië' } },
  { code: 'UG', name: { en: 'Uganda', af: 'Uganda' } },
  { code: 'AE', name: { en: 'United Arab Emirates', af: 'Verenigde Arabiese Emirate' } },
  { code: 'GB', name: { en: 'United Kingdom', af: 'Verenigde Koninkryk' } },
  { code: 'US', name: { en: 'United States', af: 'Verenigde State van Amerika' } },
  { code: 'ZM', name: { en: 'Zambia', af: 'Zambië' } },
  { code: 'ZW', name: { en: 'Zimbabwe', af: 'Zimbabwe' } },
];

export type SaProvinceCode = 'GP' | 'WC' | 'EC' | 'NC' | 'FS' | 'KZN' | 'NW' | 'MP' | 'LP';

/** The nine South African provinces (ISO 3166-2:ZA codes). */
export const SA_PROVINCES: readonly Province[] = [
  { code: 'GP', name: { en: 'Gauteng', af: 'Gauteng' } },
  { code: 'WC', name: { en: 'Western Cape', af: 'Wes-Kaap' } },
  { code: 'EC', name: { en: 'Eastern Cape', af: 'Oos-Kaap' } },
  { code: 'NC', name: { en: 'Northern Cape', af: 'Noord-Kaap' } },
  { code: 'FS', name: { en: 'Free State', af: 'Vrystaat' } },
  { code: 'KZN', name: { en: 'KwaZulu-Natal', af: 'KwaZulu-Natal' } },
  { code: 'NW', name: { en: 'North West', af: 'Noordwes' } },
  { code: 'MP', name: { en: 'Mpumalanga', af: 'Mpumalanga' } },
  { code: 'LP', name: { en: 'Limpopo', af: 'Limpopo' } },
];

/**
 * Real towns and cities per South African province (current official names;
 * well-known former names are not duplicated). Suggestions only — players may
 * type any city.
 */
const SA_CITIES: Readonly<Record<SaProvinceCode, readonly string[]>> = {
  GP: [
    'Johannesburg', 'Pretoria', 'Soweto', 'Centurion', 'Midrand', 'Sandton', 'Randburg', 'Roodepoort',
    'Krugersdorp', 'Benoni', 'Boksburg', 'Germiston', 'Kempton Park', 'Springs', 'Alberton', 'Vereeniging',
    'Vanderbijlpark', 'Tembisa', 'Randfontein', 'Heidelberg', 'Bronkhorstspruit', 'Cullinan',
  ],
  WC: [
    'Cape Town', 'Stellenbosch', 'Paarl', 'George', 'Worcester', 'Mossel Bay', 'Knysna', 'Oudtshoorn',
    'Hermanus', 'Wellington', 'Malmesbury', 'Vredenburg', 'Somerset West', 'Bellville', 'Plettenberg Bay',
    'Beaufort West', 'Swellendam', 'Robertson', 'Franschhoek', 'Caledon',
  ],
  EC: [
    'Gqeberha', 'East London', 'Mthatha', 'Makhanda', 'Kariega', 'Komani', 'Qonce', 'Graaff-Reinet',
    'Jeffreys Bay', 'Port Alfred', 'Butterworth', 'Aliwal North', 'Cradock', 'Humansdorp', 'Stutterheim',
  ],
  NC: [
    'Kimberley', 'Upington', 'Springbok', 'Kuruman', 'De Aar', 'Kathu', 'Colesberg', 'Calvinia',
    'Postmasburg', 'Prieska', 'Port Nolloth', 'Sutherland',
  ],
  FS: [
    'Bloemfontein', 'Welkom', 'Bethlehem', 'Kroonstad', 'Sasolburg', 'Parys', 'Harrismith', 'Phuthaditjhaba',
    'Botshabelo', 'Virginia', 'Ficksburg', 'Ladybrand', 'Clarens',
  ],
  KZN: [
    'Durban', 'Pietermaritzburg', 'Richards Bay', 'Newcastle', 'Ladysmith', 'Pinetown', 'Umhlanga', 'Ballito',
    'Empangeni', 'Port Shepstone', 'Margate', 'KwaDukuza', 'Howick', 'Estcourt', 'Vryheid', 'Dundee',
    'Kokstad', 'Amanzimtoti',
  ],
  NW: [
    'Mahikeng', 'Rustenburg', 'Klerksdorp', 'Potchefstroom', 'Brits', 'Vryburg', 'Lichtenburg', 'Zeerust',
    'Hartbeespoort', 'Schweizer-Reneke', 'Christiana', 'Stilfontein',
  ],
  MP: [
    'Mbombela', 'eMalahleni', 'Middelburg', 'Secunda', 'Ermelo', 'Standerton', 'Barberton', 'White River',
    'Hazyview', 'Sabie', 'Bethal', 'Komatipoort', 'Graskop', 'Delmas',
  ],
  LP: [
    'Polokwane', 'Tzaneen', 'Mokopane', 'Thohoyandou', 'Musina', 'Makhado', 'Lephalale', 'Bela-Bela',
    'Modimolle', 'Phalaborwa', 'Giyani', 'Hoedspruit', 'Thabazimbi', 'Burgersfort',
  ],
};

/** Country by ISO code (case-insensitive). */
export function findCountry(code: string): Country | undefined {
  const upper = code.trim().toUpperCase();
  return COUNTRIES.find((c) => c.code === upper);
}

/** Localised country name; unknown codes are returned as given. */
export function countryName(code: string, locale: Locale): string {
  return findCountry(code)?.name[locale] ?? code;
}

/** Countries for a picker: South Africa first, the rest sorted by their name in `locale`. */
export function sortedCountries(locale: Locale): Country[] {
  const [home, ...rest] = COUNTRIES;
  const sorted = rest.slice().sort((a, b) => a.name[locale].localeCompare(b.name[locale], locale));
  return home ? [home, ...sorted] : sorted;
}

export function isSaProvinceCode(code: string): code is SaProvinceCode {
  return SA_PROVINCES.some((p) => p.code === code);
}

/**
 * Resolve what a player or an old record stored as a province — a code or an
 * English/Afrikaans name, any case — to the South African province code.
 */
export function findSaProvince(codeOrName: string): Province | undefined {
  const upper = codeOrName.trim().toUpperCase();
  const folded = foldText(codeOrName);
  return SA_PROVINCES.find(
    (p) => p.code === upper || foldText(p.name.en) === folded || foldText(p.name.af) === folded,
  );
}

/** Display name of a stored province: SA codes are localised, free text is returned as given. */
export function provinceName(province: string, locale: Locale): string {
  return findSaProvince(province)?.name[locale] ?? province;
}

/**
 * Provinces offered for a country: the nine SA provinces for "ZA", otherwise
 * none (the UI shows a free-text field).
 */
export function provincesFor(country: string): readonly Province[] {
  return country.trim().toUpperCase() === HOME_COUNTRY ? SA_PROVINCES : [];
}

/**
 * Suggested cities for a South African province (code or name). Without a
 * province, every South African city. Other countries → [] (free text).
 */
export function citiesFor(country: string, province?: string | null): string[] {
  if (country.trim().toUpperCase() !== HOME_COUNTRY) return [];
  if (province) {
    const p = findSaProvince(province);
    return p ? [...SA_CITIES[p.code]] : [];
  }
  return SA_PROVINCES.flatMap((p) => SA_CITIES[p.code]);
}

/** Type-ahead over citiesFor(): prefix matches first, ignoring case and diacritics. */
export function searchCities(q: string, filter: { country: string; province?: string | null }, limit = 12): string[] {
  return rankMatches(citiesFor(filter.country, filter.province), q, (c) => c, limit);
}
